import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import { BackToTopButton } from './components/BackToTopButton';

// Auto-scroll to top of page on every route change so footer links
// (Privacy Policy, Terms, etc.) land the user at the top, not the bottom.
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);
  return null;
}

// Keeps <link rel="canonical"> in sync with the current route so search
// engines index each public page under its own URL.
function CanonicalUpdater() {
  const { pathname } = useLocation();
  useEffect(() => {
    const link = document.querySelector('link[rel="canonical"]');
    if (link) {
      const path = pathname === '/' ? '/' : pathname.replace(/\/+$/, '');
      link.setAttribute('href', `https://app.selahslate.com${path}`);
    }
  }, [pathname]);
  return null;
}

// Pages
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import ProfilePage from './pages/ProfilePage';
import UploadPage from './pages/UploadPage';
import DisputeBuilderPage from './pages/DisputeBuilderPage';
import MailHubPage from './pages/MailHubPage';
import DisputesPage from './pages/DisputesPage';
import DebtCalculatorPage from './pages/DebtCalculatorPage';
import FaithStrategiesPage from './pages/FaithStrategiesPage';
import AccountTimelinePage from './pages/AccountTimelinePage';
import TestimoniesPage from './pages/TestimoniesPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ScoreSimulatorPage from './pages/ScoreSimulatorPage';
import JournalPage from './pages/JournalPage';
import AdminPage from './pages/AdminPage';
import MinistryDashboardPage from './pages/MinistryDashboardPage';
import SettingsPage from './pages/SettingsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import DisclosuresPage from './pages/DisclosuresPage';
import ReflectionPage from './pages/ReflectionPage';
import SupportPage from './pages/SupportPage';
import DeleteAccountPage from './pages/DeleteAccountPage';
import ResourcesPage from './pages/ResourcesPage';
import GuidePage from './pages/GuidePage';
import BudgetPage from './pages/BudgetPage';
import BudgetPrintPage from './pages/BudgetPrintPage';
import GivingStatementPage from './pages/GivingStatementPage';
import CaseFilesPage from './pages/CaseFilesPage';
import LandingPage from './pages/LandingPage';

// Protected Route Component
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#B76E79] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#36454F]/65 font-['Manrope']">Loading...</p>
        </div>
      </div>
    );
  }

  if (user === false) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Public Route Component (redirect to dashboard if logged in)
function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#B76E79] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#36454F]/65 font-['Manrope']">Loading...</p>
        </div>
      </div>
    );
  }

  if (user && user !== false) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

// Root Route — landing page for visitors, dashboard for signed-in users.
// This is what selahslate.com / app.selahslate.com lands on first.
function RootRoute() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#B76E79] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#36454F]/65 font-['Manrope']">Loading...</p>
        </div>
      </div>
    );
  }

  if (user && user !== false) {
    return <Navigate to="/dashboard" replace />;
  }

  return <LandingPage />;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <RegisterPage />
        </PublicRoute>
      } />
      <Route path="/forgot-password" element={
        <PublicRoute>
          <ForgotPasswordPage />
        </PublicRoute>
      } />
      <Route path="/reset-password" element={
        <PublicRoute>
          <ForgotPasswordPage />
        </PublicRoute>
      } />

      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/upload" element={
        <ProtectedRoute>
          <UploadPage />
        </ProtectedRoute>
      } />
      <Route path="/dispute-builder" element={
        <ProtectedRoute>
          <DisputeBuilderPage />
        </ProtectedRoute>
      } />
      <Route path="/mail-hub" element={
        <ProtectedRoute>
          <MailHubPage />
        </ProtectedRoute>
      } />
      <Route path="/disputes" element={
        <ProtectedRoute>
          <DisputesPage />
        </ProtectedRoute>
      } />
      <Route path="/debt-calculator" element={
        <ProtectedRoute>
          <DebtCalculatorPage />
        </ProtectedRoute>
      } />
      <Route path="/faith-strategies" element={
        <ProtectedRoute>
          <FaithStrategiesPage />
        </ProtectedRoute>
      } />
      <Route path="/account-timeline" element={
        <ProtectedRoute>
          <AccountTimelinePage />
        </ProtectedRoute>
      } />
      <Route path="/testimonies" element={
        <ProtectedRoute>
          <TestimoniesPage />
        </ProtectedRoute>
      } />
      <Route path="/score-simulator" element={
        <ProtectedRoute>
          <ScoreSimulatorPage />
        </ProtectedRoute>
      } />
      <Route path="/journal" element={
        <ProtectedRoute>
          <JournalPage />
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute>
          <AdminPage />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />

      {/* Public Ministry Dashboard - no auth */}
      <Route path="/ministry/:code" element={<MinistryDashboardPage />} />

      {/* Public Legal Pages - no auth required (App Store requirement) */}
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/disclosures" element={<DisclosuresPage />} />
      <Route path="/support" element={<SupportPage />} />
      <Route path="/delete-account" element={<DeleteAccountPage />} />
      <Route path="/resources" element={<ResourcesPage />} />
      <Route path="/resources/:slug" element={<GuidePage />} />
      <Route path="/budget" element={
        <ProtectedRoute>
          <BudgetPage />
        </ProtectedRoute>
      } />
      <Route path="/budget/print/:month" element={
        <ProtectedRoute>
          <BudgetPrintPage />
        </ProtectedRoute>
      } />
      <Route path="/giving/statement/:year" element={
        <ProtectedRoute>
          <GivingStatementPage />
        </ProtectedRoute>
      } />

      {/* Financial Reflection - protected */}
      <Route path="/reflection" element={
        <ProtectedRoute>
          <ReflectionPage />
        </ProtectedRoute>
      } />

      {/* Print Station & Case Files - protected */}
      <Route path="/case-files" element={
        <ProtectedRoute>
          <CaseFilesPage />
        </ProtectedRoute>
      } />

      {/* Root: marketing landing for visitors, dashboard for signed-in users */}
      <Route path="/" element={<RootRoute />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ScrollToTop />
        <CanonicalUpdater />
        <AppRoutes />
        <BackToTopButton />
        <Toaster position="top-right" offset={80} mobileOffset={{ top: 80, right: 12, left: 12 }} />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
